import type { ApolloClient } from "@apollo/client";
import type { Dispatch } from "redux";
import { partition } from "lodash";

import { documentConstants } from "./types";
import { getDocuments } from "services/documentService";
import ActionCreator from "utils/actionHandler";

// Apex issues trade confirmations under the same account as IRA statements/tax
// documents, so a single APEX fetch already contains the confirmations. This
// lets us split that one response client-side instead of issuing a second,
// redundant request for the confirmations tab.
const TRADE_CONFIRMATION_TYPE = "TradeConfirmation";

export const getAllDocuments = (
  client: ApolloClient<object>,
  accountId: string
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof getDocuments>>
    >(documentConstants.GET_DOCUMENTS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      getDocuments(client, accountId)
    );
  };
};

export type DocumentBucket =
  | "prpStatements"
  | "iraStatements"
  | "confirmations";

type BucketDocs = Awaited<ReturnType<typeof getDocuments>>;

interface BucketedDocuments {
  bucket: DocumentBucket;
  docs: BucketDocs;
}

// Documents keyed by more than one destination bucket in a single dispatch —
// used when one fetch feeds multiple tabs (see getApexDocuments).
export interface BucketedDocumentGroups {
  buckets: Partial<Record<DocumentBucket, BucketDocs>>;
}

// Fetch one tab's documents (filtered to a single provider) and store them
// under a dedicated bucket so tab switches don't clobber or refetch other tabs.
export const getDocumentsForBucket = (
  client: ApolloClient<object>,
  accountId: string,
  bucket: DocumentBucket,
  provider?: string
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<BucketedDocuments>(
      documentConstants.GET_DOCUMENTS
    );
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      getDocuments(client, accountId, provider).then((docs) => ({
        bucket,
        docs,
      }))
    );
  };
};

// Fetch every APEX document once and split it into the IRA statements bucket
// (statements + tax documents) and the confirmations bucket (trade
// confirmations), avoiding a second round-trip for the confirmations tab.
export const getApexDocuments = (
  client: ApolloClient<object>,
  accountId: string
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<BucketedDocumentGroups>(
      documentConstants.GET_DOCUMENTS
    );
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      getDocuments(client, accountId, "APEX").then((docs) => {
        const [confirmations, iraStatements] = partition(
          docs ?? [],
          (doc) => doc?.type === TRADE_CONFIRMATION_TYPE
        );
        return { buckets: { iraStatements, confirmations } };
      })
    );
  };
};

export default {
  getAllDocuments,
  getDocumentsForBucket,
  getApexDocuments,
};
